import { Drawer, Select, Input, Switch, Button, Form } from "antd";
import { ArrowRightLeft, Bell } from "lucide-react";
import { useSelector } from "react-redux";
import { userRoles } from "../../config";

const { TextArea } = Input;

const TransferLeadsDrawer = ({
  open,
  onClose,
  onSubmit,
  selectedCount = 0,
  currentOwner = "",
  isAdmin,
  selectedRecords,
  user,
}) => {
  const [form] = Form.useForm();
  const currentUserPermissions = useSelector(
    (state) => state.UserManagementReducer.currentUserPermissions,
  );
  const users = useSelector((state) => state.UserManagementReducer.users);

  const handleFinish = (values) => {
    onSubmit({
      ...values,
      newOwnerName:
        users?.content?.find((d) => d?.id === values?.newOwnerId)?.firstName +
        " " +
        users?.content?.find((d) => d?.id === values?.newOwnerId)?.lastName,
    });
    form.resetFields();
    onClose();
  };

  const nonTransferAble = selectedRecords?.filter(
    (d) => !d?.executiveData?.allowedActions?.includes("TRANSFER"),
  );

  return (
    <Drawer
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      width={420}
      placement="right"
      title={
        <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-0">
          Transfer Leads ({selectedCount})
        </h3>
      }
      footerStyle={{ padding: 0 }}
      footer={
        <div className="tw-flex tw-justify-end tw-gap-3 tw-py-3 tw-px-6 tw-bg-white tw-border-t tw-border-solid tw-border-slate-100">
          <Button
            className="!tw-h-[38px] !tw-rounded-xl tw-text-sm tw-text-slate-600 hover:tw-text-slate-800 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-slate-400 tw-transition-all tw-px-5"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="theme-btn !tw-h-[38px] !tw-rounded-xl tw-font-bold tw-text-sm tw-shadow-[0_8px_20px_rgba(93,68,255,0.25)] hover:tw-shadow-[0_12px_24px_rgba(93,68,255,0.35)] hover:tw-scale-[1.01] tw-transition-all tw-duration-300 tw-px-6 tw-flex tw-items-center tw-gap-1.5"
            onClick={() => form.submit()}
          >
            <ArrowRightLeft size={16} /> Confirm Transfer
          </Button>
        </div>
      }
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        initialValues={{
          notify: true,
        }}
        requiredMark={false}
      >
        {/* Selected Leads */}
        <div className="tw-mb-4">
          <div className="tw-text-xs tw-font-bold tw-text-slate-500 tw-mb-2">
            Selected Leads
          </div>

          <div className="tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-100 tw-rounded-xl tw-p-4 tw-text-text-primary">
            <div className="tw-font-bold tw-text-slate-800 tw-text-sm">
              {selectedCount} Leads Selected
            </div>
            <div className="tw-text-xs tw-text-slate-500 tw-mt-1">
              Current Owner : {currentOwner}
            </div>
            {!!nonTransferAble?.length && (
              <div className="tw-mt-2 tw-text-xs tw-text-red-500 tw-font-semibold">
                Transfer permission is available for{" "}
                {(Number(selectedRecords?.length) || 0) -
                  (Number(nonTransferAble?.length) || 0)}{" "}
                of the selected leads in your account. The remaining leads
                cannot be transferred.
              </div>
            )}
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-items-center tw-my-4">
          {/* Top dashed line */}
          <div className="tw-h-6 tw-border-l tw-border-r-0 tw-border-t-0 tw-border-b-0 tw-border-dashed tw-border-slate-300" />

          {/* Arrow */}
          <div className="tw-my-1 tw-text-slate-400">
            <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
              <path
                d="M8 0V14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M3 9L8 14L13 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Bottom dashed line */}
          <div className="tw-h-6 tw-border-l tw-border-r-0 tw-border-t-0 tw-border-b-0 tw-border-dashed tw-border-slate-300" />
        </div>

        {/* Transfer To */}
        <Form.Item
          label={
            <span className="tw-text-xs tw-font-bold tw-text-slate-600">
              Transfer to<span className="tw-text-red-500 tw-ml-0.5">*</span>
            </span>
          }
          name="newOwnerId"
          rules={[{ required: true, message: "Please select new owner" }]}
        >
          <Select
            placeholder="Select New Owner"
            popupClassName="!tw-rounded-xl"
            className="tw-select-modern tw-w-full"
            notFoundContent={
              <span className="tw-text-xs tw-text-sub-text">
                {isAdmin
                  ? "You do not have any internal users. Please create users to use this feature."
                  : "You don’t have permission to transfer lead. Please contact your admin for access."}
              </span>
            }
          >
            {isAdmin ? (
              <>
                {(users?.content || [])
                  ?.filter((d) => d?.id !== user?.id)
                  ?.filter((d) => {
                    return user?.allowTransferToAdmin
                      ? !!d?.role
                      : d?.role !== userRoles?.admin;
                  })
                  .map((u) => (
                    <Select.Option key={u?.id} value={u?.id}>
                      {u.firstName} {u.lastName}
                    </Select.Option>
                  ))}
              </>
            ) : (
              <>
                {(user?.allowTransferToAdmin
                  ? [
                      ...(currentUserPermissions || []),
                      {
                        targetUser: {
                          ...users?.content?.find(
                            (d) => d?.role === userRoles.admin,
                          ),
                        },
                        permission: {
                          id: 8,
                          name: "Transfer",
                          internalName: "transfer",
                          createdDate: null,
                          lastModifiedDate: null,
                        },
                      },
                    ] || []
                  : currentUserPermissions
                )
                  ?.filter((d) => d?.permission?.name === "Transfer")
                  .map((user) => (
                    <Select.Option
                      key={user?.targetUser?.id}
                      value={user?.targetUser?.id}
                    >
                      {user?.targetUser.firstName} {user?.targetUser.lastName}
                    </Select.Option>
                  ))}
              </>
            )}
          </Select>
        </Form.Item>

        {/* Transfer Note */}
        <Form.Item
          label={
            <span className="tw-text-xs tw-font-bold tw-text-slate-600">
              Transfer note (Optional)
            </span>
          }
          name="transferNote"
        >
          <TextArea
            className="!tw-bg-amber-100/40 !tw-border-slate-200/80 focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(99,102,241,0.1)] !tw-rounded-xl tw-p-3.5 tw-text-slate-700 placeholder:tw-text-slate-400 tw-transition-all"
            rows={4}
            placeholder="Add a note about this transfer..."
          />
        </Form.Item>

        {/* Notify Switch */}
        <Form.Item name="isNotify" valuePropName="checked" className="tw-mb-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-xl tw-px-4 tw-py-3.5 tw-transition-all">
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700">
              <Bell size={16} className="tw-text-slate-400" />
              <span className="tw-text-xs tw-font-bold">Notify New Owner</span>
            </div>
            <Switch
              onChange={(val) => form.setFieldsValue({ isNotify: val })}
            />
          </div>
        </Form.Item>

        {/* Warning */}
        <div className="tw-bg-amber-50/50 tw-text-amber-700 tw-text-xs tw-font-medium tw-p-3.5 tw-rounded-xl tw-border tw-border-solid tw-border-amber-200/80 tw-flex tw-items-center tw-gap-2">
          This will update ownership for all {selectedCount} selected leads
        </div>
      </Form>
    </Drawer>
  );
};

export default TransferLeadsDrawer;
